import React from 'react';
import { Badge, Button, Flex, Menu, MenuButton, MenuDivider, MenuItem, MenuItemOption, MenuList, MenuOptionGroup, Text } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

// Largura unica para os dois filtros do Despacho: no celular ocupam a linha
// inteira, no desktop ficam do mesmo tamanho do campo de busca ao lado. Quem
// vive dentro de um grid passa '100%' e deixa a coluna mandar.
export const LARGURA_FILTRO = { base: '100%', md: '250px' };

/**
 * Filtro de selecao multipla com checkbox (#2418).
 *
 * Fechado, se le como o Select que existia antes: nada marcado mostra o texto
 * de "todos", um marcado mostra o nome dele. A primeira opcao da lista continua
 * sendo esse "todos", que agora serve para limpar tudo de uma vez em vez de
 * desmarcar item por item.
 *
 * A borda fica laranja quando ha algo marcado. Sem esse sinal, um filtro
 * esquecido faz a tela parecer vazia sem motivo aparente.
 *
 * `opcoes` aceita string ou { valor, rotulo } — os modos de envio vem do
 * proprio dado e as origens tem rotulo proprio ("BLPARTS" vira "BL Parts").
 */
const FiltroMultiplo = ({
    rotuloTodos, opcoes, selecionados, onChange,
    largura = LARGURA_FILTRO, size,
    bg = 'white', borderColor = 'inherit', borderRadius,
}) => {
    const itens = opcoes.map(o => (typeof o === 'string' ? { valor: o, rotulo: o } : o));
    const ativo = selecionados.length > 0;
    const rotulo = !ativo
        ? rotuloTodos
        : (selecionados.length === 1
            ? (itens.find(i => i.valor === selecionados[0])?.rotulo ?? selecionados[0])
            : `${selecionados.length} selecionados`);

    return (
        <Menu closeOnSelect={false} autoSelect={false} placement="bottom-start">
            <MenuButton
                as={Button}
                size={size}
                w={largura}
                flexShrink={0}
                px={4}
                bg={bg}
                borderWidth="1px"
                borderColor={ativo ? 'orange.300' : borderColor}
                borderRadius={borderRadius}
                fontWeight="normal"
                textAlign="left"
                _hover={{ bg, borderColor: 'orange.300' }}
                _active={{ bg }}
                _expanded={{ bg, borderColor: 'orange.400' }}
            >
                <Flex align="center" gap={2} w="100%">
                    <Text isTruncated flex="1" fontSize="sm" color={ativo ? 'gray.800' : 'gray.500'}>
                        {rotulo}
                    </Text>
                    {selecionados.length > 1 && (
                        <Badge colorScheme="orange" borderRadius="full" px={2} flexShrink={0}>
                            {selecionados.length}
                        </Badge>
                    )}
                    <ChevronDownIcon flexShrink={0} color="gray.500" />
                </Flex>
            </MenuButton>

            {/* No celular a lista precisa caber na tela e rolar sozinha; a altura
                em vh evita que uma lista longa empurre o conteudo para fora. */}
            <MenuList
                minW="250px"
                maxW="calc(100vw - 2rem)"
                maxH={{ base: '60vh', md: '340px' }}
                overflowY="auto"
                zIndex={20}
                py={1}
            >
                <MenuItem
                    onClick={() => onChange([])}
                    py={2}
                    fontSize="sm"
                    fontWeight={ativo ? 'normal' : 'semibold'}
                    color={ativo ? 'gray.600' : 'gray.800'}
                >
                    {rotuloTodos}
                </MenuItem>
                <MenuDivider my={1} />
                <MenuOptionGroup
                    type="checkbox"
                    value={selecionados}
                    onChange={(v) => onChange(Array.isArray(v) ? v : [v])}
                >
                    {itens.map(({ valor, rotulo: texto }) => (
                        <MenuItemOption key={valor} value={valor} py={2} fontSize="sm">{texto}</MenuItemOption>
                    ))}
                </MenuOptionGroup>
            </MenuList>
        </Menu>
    );
};

export default FiltroMultiplo;
